// filepath: /Users/a1111/WebstormProjects/arch-editor/src/pages/ProjectDetails.js
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDiagrams, deleteDiagram } from '../api/diagramsApi';
import { listRegistryTypes } from '../api/metaRegistriesApi';
import { useProject } from '../context/ProjectContext';
import { useTheme } from '../hooks/useTheme';
import Header from '../components/Header';
import NavigationRail, { NAV_RAIL_COLLAPSED_WIDTH } from '../components/navigation/NavigationRail';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const DOMAINS = [
  'Architecture Vision',
  'Business Architecture',
  'Information Systems Architecture',
  'Technology Architecture'
];

const normalizeDomain = (d) => (DOMAINS.includes(d) ? d : 'Architecture Vision');

const SLUG_ICONS = {
  information_systems: <DnsOutlinedIcon fontSize="small" />,
  'business-capabilities': <ExtensionOutlinedIcon fontSize="small" />,
  'bcm-maps': <MapOutlinedIcon fontSize="small" />,
};

const FALLBACK_ICONS = [
  <CategoryOutlinedIcon fontSize="small" />,
  <LayersOutlinedIcon fontSize="small" />,
  <HubOutlinedIcon fontSize="small" />,
  <SchemaOutlinedIcon fontSize="small" />,
  <ListAltOutlinedIcon fontSize="small" />,
  <WidgetsOutlinedIcon fontSize="small" />,
  <AssignmentOutlinedIcon fontSize="small" />,
  <AccountTreeOutlinedIcon fontSize="small" />,
];

const getRegistryIcon = (slug, index = 0) => {
  if (SLUG_ICONS[slug]) return SLUG_ICONS[slug];
  return FALLBACK_ICONS[index % FALLBACK_ICONS.length];
};

const ProjectDetails = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { theme } = useTheme();
  const { currentProject, syncProjectFromId } = useProject();
  const [railWidth, setRailWidth] = useState(NAV_RAIL_COLLAPSED_WIDTH);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [diagrams, setDiagrams] = useState([]);
  const [query, setQuery] = useState('');
  const [showBoardTypeModal, setShowBoardTypeModal] = useState(false);
  const [projectMetamodels, setProjectMetamodels] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Pre-populate with IS registry so the button is immediately visible;
  // the API call below replaces this with the full list.
  const [registryTypes, setRegistryTypes] = useState([
    { slug: 'information_systems', name: 'Информационные системы', description: '' },
  ]);

  const defaultBoardTypes = [
    { id: 'free', label: 'Свободная', pathType: 'free' },
    { id: 'uml', label: 'UML', pathType: 'uml' },
    { id: 'c4', label: 'C4', pathType: 'c4' },
    { id: 'archimate', label: 'Archimate', pathType: 'archimate' },
    { id: 'bpmn', label: 'BPMN 2.0', pathType: 'bpmn' },
    { id: 'erd', label: 'ERD (PlantUML)', pathType: 'erd' },
  ];

  useEffect(() => {
    syncProjectFromId(projectId);
  }, [projectId, syncProjectFromId]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getDiagrams(projectId)
      .then((list) => {
        if (mounted) {
          console.log('Загружено диаграмм:', list.length, list);
          setDiagrams(list);
        }
      })
      .catch((e) => {
        if (mounted) {
          console.error('Ошибка загрузки диаграмм:', e);
          setError(e.message || String(e));
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [projectId]);

  useEffect(() => {
    listRegistryTypes(projectId).then(setRegistryTypes).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!showBoardTypeModal || !projectId) return;
    import('../api/metamodelsApi').then(({ listMetamodels }) =>
      listMetamodels(projectId).then(setProjectMetamodels).catch(() => setProjectMetamodels([]))
    );
  }, [showBoardTypeModal, projectId]);

  const grouped = useMemo(() => {
    const byDomain = new Map(DOMAINS.map((d) => [d, []]));
    const q = query.trim().toLowerCase();
    diagrams.forEach((d) => {
      const name = (d.meta?.name || '').toLowerCase();
      if (q && !name.includes(q)) return;
      const dom = normalizeDomain(d.meta?.domain);
      byDomain.get(dom).push(d);
    });
    DOMAINS.forEach((dom) => {
      byDomain.get(dom).sort((a, b) => new Date(b.meta?.updatedAt || 0) - new Date(a.meta?.updatedAt || 0));
    });
    return byDomain;
  }, [diagrams, query]);

  const registryRailItems = useMemo(() => {
    const dedicated = { information_systems: 'systems' };
    const seen = new Set();
    const items = registryTypes
      .filter((rt) => {
        if (seen.has(rt.slug)) return false;
        seen.add(rt.slug);
        return true;
      })
      .map((rt, idx) => {
        const path = rt.slug === 'bcm-maps'
          ? `/projects/${encodeURIComponent(projectId)}/bcm`
          : dedicated[rt.slug]
          ? `/projects/${encodeURIComponent(projectId)}/${dedicated[rt.slug]}`
          : `/projects/${encodeURIComponent(projectId)}/registry/${encodeURIComponent(rt.slug)}`;

        return {
          id: rt.slug,
          label: rt.name,
          icon: getRegistryIcon(rt.slug, idx),
          active: false,
          onClick: () => navigate(path),
        };
      });

    items.push({
      id: 'health-map',
      label: 'Карта работоспособности',
      icon: <MonitorHeartOutlinedIcon fontSize="small" />,
      active: false,
      onClick: () => navigate(`/projects/${encodeURIComponent(projectId)}/health`),
    });

    const role = currentProject?.role;
    if (role === 'owner' || role === 'admin') {
      items.push({
        id: 'settings',
        label: 'Настройки пространства',
        icon: <SettingsOutlinedIcon fontSize="small" />,
        active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId)}/settings`),
      });
    }

    return items;
  }, [registryTypes, projectId, navigate, currentProject?.role]);

  const isDark = theme.name === 'dark';
  const pageBackground = isDark
    ? 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%), #0f1419'
    : 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 32%), #f5f7fa';
  const surface = isDark ? '#151b23' : '#ffffff';
  const surfaceSoft = isDark ? '#111827' : '#f8fafc';
  const border = isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.28)';
  const mutedText = isDark ? '#94a3b8' : '#64748b';
  const primaryActionSx = {
    minHeight: 42,
    px: 2.25,
    borderRadius: '8px',
    border: '1px solid #2563eb',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 14,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#1d4ed8',
      borderColor: '#1d4ed8',
      boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
    },
  };
  const secondaryActionSx = {
    minHeight: 42,
    borderRadius: '8px',
    color: theme.toolbar.text,
    borderColor: border,
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
      borderColor: '#2563eb',
      backgroundColor: isDark ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.08)',
    },
  };
  const textFieldSx = {
    width: { xs: '100%', md: 420 },
    '& .MuiOutlinedInput-root': {
      minHeight: 42,
      borderRadius: '8px',
      backgroundColor: surface,
      color: theme.toolbar.text,
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: '#2563eb' },
      '&.Mui-focused fieldset': { borderColor: '#2563eb' },
    },
    '& input::placeholder': { color: mutedText, opacity: 1 },
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDiagram(projectId, deleteTarget.id);
      setDiagrams((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Ошибка удаления диаграммы:', err);
    } finally {
      setDeleting(false);
    }
  };

  const openBoard = (type, metamodelId) => {
    setShowBoardTypeModal(false);
    const params = new URLSearchParams({
      type,
      project: projectId,
    });
    if (metamodelId) params.set('metamodelId', metamodelId);
    navigate(`/editor?${params.toString()}`);
  };

  return (
    <>
      <Header title="Aspect" height={56} />
      <NavigationRail
        ariaLabel="Реестры пространства"
        theme={theme}
        items={registryRailItems}
        onExpandedChange={setRailWidth}
      />
      <Box
        data-testid="project-details-content"
        style={{
          marginLeft: railWidth,
          width: `calc(100% - ${railWidth}px)`,
        }}
        sx={{
          px: { xs: 2, md: 3 },
          pt: '88px',
          pb: 4,
          background: pageBackground,
          minHeight: '100vh',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: 'margin-left .16s ease, width .16s ease',
        }}
      >
        <Box sx={{ maxWidth: 1600, mx: 'auto', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h1" sx={{ color: theme.toolbar.text, fontWeight: 800, letterSpacing: 0 }}>
                {currentProject?.name || projectId}
              </Typography>
              <Typography sx={{ color: mutedText, mt: 0.75, fontSize: 14 }}>
                Диаграммы пространства по архитектурным доменам.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={secondaryActionSx}>
                Назад
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowBoardTypeModal(true)} sx={primaryActionSx}>
                Новая диаграмма
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 2,
            }}
          >
            <Chip
              label={`${diagrams.length} диаграмм`}
              sx={{
                width: 'fit-content',
                borderRadius: '8px',
                backgroundColor: surface,
                color: mutedText,
                border: `1px solid ${border}`,
                fontWeight: 700,
              }}
            />
            <TextField
              type="search"
              placeholder="Поиск по названию"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              size="small"
              sx={textFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: mutedText }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading && <Typography sx={{ color: theme.toolbar.text }}>Загрузка…</Typography>}
          {error && <Typography role="alert" sx={{ color: '#ff6b6b' }}>Ошибка: {error}</Typography>}

          {!loading && !error && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 2,
              }}
            >
              {DOMAINS.map((dom, domainIndex) => (
                <Fade in timeout={220 + domainIndex * 60} key={dom}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${border}`,
                      background: surface,
                      borderRadius: '8px',
                      p: 1.5,
                      minHeight: 180,
                    }}
                  >
                    <Typography sx={{ color: theme.toolbar.text, fontWeight: 800, fontSize: 16, mb: 1 }}>
                      {dom}
                    </Typography>
                    {grouped.get(dom).length === 0 ? (
                      <Box
                        sx={{
                          border: `1px dashed ${border}`,
                          borderRadius: '8px',
                          p: 2,
                          color: mutedText,
                          backgroundColor: surfaceSoft,
                          fontSize: 14,
                        }}
                      >
                        Пусто
                      </Box>
                    ) : (
                      grouped.get(dom).map((d) => (
                        <Card
                          key={d.id}
                          elevation={0}
                          sx={{
                            position: 'relative',
                            mb: 1,
                            border: `1px solid ${border}`,
                            borderRadius: '8px',
                            background: surfaceSoft,
                            overflow: 'hidden',
                            transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                            '&:hover': {
                              transform: 'translateY(-3px)',
                              borderColor: '#2563eb',
                              boxShadow: isDark ? '0 14px 30px rgba(0, 0, 0, 0.28)' : '0 14px 30px rgba(15, 23, 42, 0.10)',
                            },
                          }}
                        >
                          <CardActionArea
                            component="div"
                            onClick={() => navigate(`/editor/${encodeURIComponent(d.id)}?type=${d.meta?.type || 'uml'}&project=${encodeURIComponent(projectId)}`)}
                            title="Открыть диаграмму"
                          >
                            <CardContent sx={{ pr: 5.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Typography
                                sx={{
                                  color: theme.toolbar.text,
                                  fontWeight: 700,
                                  fontSize: 14,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {d.meta?.name || d.id}
                              </Typography>
                              <Typography sx={{ color: mutedText, fontSize: 12, mt: 0.5 }}>
                                Версия: {d.meta?.version ?? '-'} · Обновлено: {d.meta?.updatedAt ? new Date(d.meta.updatedAt).toLocaleString() : '-'}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(d); }}
                            title="Удалить диаграмму"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              color: '#ef4444',
                              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Card>
                      ))
                    )}
                  </Card>
                </Fade>
              ))}
            </Box>
          )}

          <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
            <DialogTitle>Удалить диаграмму?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                «{deleteTarget?.meta?.name || deleteTarget?.id}» будет удалена навсегда. Это действие нельзя отменить.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Отмена</Button>
              <Button onClick={handleDeleteConfirm} color="error" disabled={deleting} autoFocus>
                {deleting ? 'Удаление…' : 'Удалить'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={showBoardTypeModal} onClose={() => setShowBoardTypeModal(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Выберите тип доски</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {defaultBoardTypes.map((board) => (
                  <Button key={board.id} variant="outlined" onClick={() => openBoard(board.pathType)} sx={secondaryActionSx}>
                    {board.label}
                  </Button>
                ))}
                {projectMetamodels.length > 0 && (
                  <>
                    <Typography sx={{ mt: 1, color: mutedText, fontSize: 12, fontWeight: 700 }}>
                      Кастомные метамодели
                    </Typography>
                    {projectMetamodels.map((mm) => (
                      <Button key={mm.id} variant="outlined" onClick={() => openBoard('free', mm.id)} sx={secondaryActionSx}>
                        {mm.name}
                      </Button>
                    ))}
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowBoardTypeModal(false)}>Отмена</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </>
  );
};

export default ProjectDetails;
